<template>
  <div>
    <!-- Filtros -->
    <b-card class="mb-2">
      <b-card-header><h5 class="mb-0">Razão por Conta (Todos os Níveis)</h5></b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="3">
            <b-form-group label="Data Inicial">
              <b-form-input v-model="filtro.dataInicio" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3">
            <b-form-group label="Data Final">
              <b-form-input v-model="filtro.dataFim" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4" class="d-flex align-items-center">
            <b-form-checkbox v-model="filtro.incluirConsolidado">
              Totais consolidados (conta + subcontas)
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-end">
            <b-button variant="primary" class="w-100" :disabled="loading" @click="buscar">
              <b-spinner v-if="loading" small class="mr-1" />
              <feather-icon v-else icon="FileTextIcon" class="mr-1" />
              Gerar
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Tabela em Árvore -->
    <b-card v-if="dados">
      <b-card-header class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">Razão Hierárquico</h5>
          <small class="text-muted">{{ formatDate(dados.dataInicio) }} a {{ formatDate(dados.dataFim) }}</small>
        </div>
        <div class="d-flex gap-2">
          <b-button variant="outline-secondary" size="sm" class="mr-1" @click="expandirTodos">
            <feather-icon icon="MaximizeIcon" class="mr-1" />Expandir
          </b-button>
          <b-button variant="outline-secondary" size="sm" class="mr-1" @click="recolherTodos">
            <feather-icon icon="MinimizeIcon" class="mr-1" />Recolher
          </b-button>
          <b-button variant="success" size="sm" @click="exportarCsv">
            <feather-icon icon="DownloadIcon" class="mr-1" />CSV
          </b-button>
          <b-button variant="danger" size="sm" class="ml-1" @click="imprimirPdf" :disabled="loadingPdf">
            <b-spinner v-if="loadingPdf" small class="mr-1" />
            <feather-icon v-else icon="PrinterIcon" class="mr-1" />PDF
          </b-button>
        </div>
      </b-card-header>
      <b-card-body class="p-0">
        <div class="table-responsive">
          <table class="table table-bordered table-sm mb-0">
            <thead>
              <tr>
                <th style="width:15%">Código</th>
                <th>Plano de Conta</th>
                <th class="text-right" style="width:18%">Total Receitas</th>
                <th class="text-right" style="width:18%">Total Despesas</th>
                <th class="text-right" style="width:18%">Saldo</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(node, idx) in aplanados">
                <tr
                  :key="node.id"
                  :data-id="node.id"
                  :data-level="node.__level"
                  :style="{ display: node.__visible ? '' : 'none' }"
                  class="razao-row"
                >
                  <td :style="{ paddingLeft: (node.__level * 20 + 8) + 'px' }">
                    <span
                      v-if="node.__temFilhos"
                      class="cursor-pointer mr-1"
                      @click="toggle(node)"
                    >
                      <feather-icon :icon="node.__expanded ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="14" />
                    </span>
                    <span class="font-weight-medium">{{ node.codigo }}</span>
                  </td>
                  <td>{{ node.nome }}</td>
                  <td class="text-right text-success">{{ formatMoeda(node.totalReceitas) }}</td>
                  <td class="text-right text-danger">{{ formatMoeda(node.totalDespesas) }}</td>
                  <td class="text-right" :class="node.saldo >= 0 ? 'text-success' : 'text-danger'">{{ formatMoeda(node.saldo) }}</td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr class="font-weight-bold table-active">
                <td colspan="2">TOTAL</td>
                <td class="text-right text-success">{{ formatMoeda(dados.totalReceitas) }}</td>
                <td class="text-right text-danger">{{ formatMoeda(dados.totalDespesas) }}</td>
                <td class="text-right" :class="dados.saldoTotal >= 0 ? 'text-success' : 'text-danger'">{{ formatMoeda(dados.saldoTotal) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormInput,
  BButton, BSpinner, BFormCheckbox
} from 'bootstrap-vue'
import contabilServices from '@/api/financialModule/contabilServices'
import mixinsGlobal from '@/mixins'

export default {
  name: 'RazaoHierarquicoReport',
  components: { BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormInput, BButton, BSpinner, BFormCheckbox },
  mixins: [mixinsGlobal],
  data() {
    const hoje = new Date()
    return {
      filtro: {
        dataInicio: `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`,
        dataFim: hoje.toISOString().substring(0, 10),
        incluirConsolidado: false
      },
      dados: null,
      aplanados: [],
      loading: false,
      loadingPdf: false
    }
  },
  methods: {
    async buscar() {
      this.loading = true
      try {
        const res = await contabilServices.getRazaoHierarquico({
          dataInicio: this.filtro.dataInicio,
          dataFim: this.filtro.dataFim,
          incluirConsolidado: this.filtro.incluirConsolidado
        })
        this.dados = res.data
        this.aplanados = this.aplanar(res.data.arvore || [], 0, true)
      } catch {
        this.MensagemError('Erro', 'Falha ao gerar Razão Hierárquico.')
      } finally { this.loading = false }
    },
    aplanar(nos, nivel, visivel) {
      const lista = []
      for (const n of nos) {
        const temFilhos = n.filhos && n.filhos.length > 0
        const node = { ...n, __level: nivel, __expanded: false, __visible: visivel, __temFilhos: temFilhos }
        lista.push(node)
        if (temFilhos) {
          lista.push(...this.aplanar(n.filhos, nivel + 1, false))
        }
      }
      return lista
    },
    toggle(node) {
      node.__expanded = !node.__expanded
      this.setChildrenVisible(node, node.__expanded)
    },
    setChildrenVisible(pai, visivel) {
      const filhosIds = new Set((pai.filhos || []).map(f => f.id))
      for (const node of this.aplanados) {
        if (filhosIds.has(node.id)) {
          node.__visible = visivel
          if (!visivel) {
            node.__expanded = false
            this.setChildrenVisible(node, false)
          }
        }
      }
    },
    expandirTodos() {
      this.aplanados.forEach(n => { n.__visible = true; n.__expanded = true })
    },
    recolherTodos() {
      this.aplanados.forEach(n => { n.__expanded = false; n.__visible = n.__level === 0 })
    },
    formatDate(d) {
      if (!d) return ''
      const parts = Array.isArray(d) ? d : String(d).split('-')
      return `${String(parts[2]).padStart(2,'0')}/${String(parts[1]).padStart(2,'0')}/${parts[0]}`
    },
    formatMoeda(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
    },
    exportarCsv() {
      if (!this.dados) return
      const h = ['Código', 'Plano de Conta', 'Total Receitas', 'Total Despesas', 'Saldo']
      const rows = this.aplanados.map(n => [n.codigo, n.nome, n.totalReceitas, n.totalDespesas, n.saldo])
      rows.push(['TOTAL', '', this.dados.totalReceitas, this.dados.totalDespesas, this.dados.saldoTotal])
      const csv = [h, ...rows].map(r => r.join(';')).join('\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = 'razao_hierarquico.csv'; a.click()
    },
    imprimirPdf() {
      if (!this.dados) return
      this.loadingPdf = true
      this.$store.dispatch('reportModule/printReportRazaoHierarquico', {
        ...this.dados,
        aplanados: this.aplanados
      }).then(res => {
        if (res.error) this.MensagemError('Erro', res.error)
      }).finally(() => {
        this.loadingPdf = false
      })
    }
  }
}
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }
</style>
